// Path in repo: src/pages/organization-dashboard/components/OnChainCredentialsSection.jsx
//
// Shared component for "On-chain Credential Activity".
// - variant="compact": used embedded on the Dashboard overview, directly
//   below the Proof of Learning funnel. Whole card is clickable and jumps
//   to the "credentials-onchain" tab via onNavigateTab.
// - variant="full": used as the panel body when the "credentials-onchain"
//   tab is active.
//
// Data contract (no new Firestore collection — fields live on the
// `organization` doc so there's nothing to migrate later):
//   organization.onChainEnabled: boolean            (undefined/false today for every org)
//   organization.onChainStats: {
//     credentialsAnchored: number,
//     verificationRequests: number,
//     onChainTransactions: number,
//     networkStatus: "operational" | "degraded" | "down",
//   }
//
// Until a blockchain partner is connected, onChainEnabled stays false for
// every org and both variants render the empty/CTA state. The enabled
// branch is wired now so nothing needs to change here later — flipping
// onChainEnabled to true and populating onChainStats is enough.

import React from "react"

const STATUS_STYLES = {
  operational: { dot: "bg-emerald-500", label: "Operational" },
  degraded: { dot: "bg-amber-500", label: "Degraded" },
  down: { dot: "bg-red-500", label: "Down" },
}

const StatCard = ({ label, value }) => (
  <div className="rounded-lg border border-border bg-muted/40 p-4">
    <p className="text-sm text-muted-foreground">{label}</p>
    <p className="mt-1 text-2xl font-semibold text-foreground">{value}</p>
  </div>
)

const EmptyState = ({ variant, onNavigateTab }) => {
  const isCompact = variant === "compact"

  return (
    <div
      className={
        isCompact
          ? "flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between"
          : "flex flex-col items-start gap-4"
      }
    >
      <div>
        <p className="font-medium text-foreground">
          On-chain credentialing isn't enabled
        </p>
        <p className="mt-1 text-sm text-muted-foreground">
          {isCompact
            ? "Anchor issued credentials on-chain once a blockchain partner is connected."
            : "No blockchain network is connected yet. Once one is, credentials issued by your organization can be anchored on-chain and independently verified without going through Nesolearn."}
        </p>
      </div>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation()
          onNavigateTab?.("credentials-onchain")
        }}
        className="shrink-0 self-start rounded-md border border-border bg-card px-3 py-2 text-sm font-medium text-foreground hover:bg-muted transition-colors"
      >
        Enable credential infrastructure →
      </button>
    </div>
  )
}

const EnabledState = ({ stats, variant }) => {
  const status = STATUS_STYLES[stats?.networkStatus] || STATUS_STYLES.operational

  return (
    <div className="space-y-4">
      <div
        className={
          variant === "compact"
            ? "grid grid-cols-2 gap-3 sm:grid-cols-4"
            : "grid grid-cols-1 gap-4 sm:grid-cols-3"
        }
      >
        <StatCard
          label="Credentials Anchored"
          value={(stats?.credentialsAnchored ?? 0).toLocaleString()}
        />
        <StatCard
          label="Verification Requests"
          value={(stats?.verificationRequests ?? 0).toLocaleString()}
        />
        <StatCard
          label="On-chain Transactions"
          value={(stats?.onChainTransactions ?? 0).toLocaleString()}
        />
        {variant === "full" && (
          <div className="rounded-lg border border-border bg-muted/40 p-4">
            <p className="text-sm text-muted-foreground">Network Status</p>
            <p className="mt-1 flex items-center gap-2 text-2xl font-semibold text-foreground">
              <span className={`inline-block h-2.5 w-2.5 rounded-full ${status.dot}`} />
              {status.label}
            </p>
          </div>
        )}
      </div>

      {variant === "compact" && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <span className={`inline-block h-2 w-2 rounded-full ${status.dot}`} />
          Network {status.label.toLowerCase()}
        </div>
      )}
    </div>
  )
}

const OnChainCredentialsSection = ({ organization, onNavigateTab, variant = "full" }) => {
  const enabled = Boolean(organization?.onChainEnabled)
  const isCompact = variant === "compact"

  const containerClasses = isCompact
    ? "rounded-lg border border-border bg-card p-5 shadow-sm cursor-pointer hover:border-primary/40 transition-colors"
    : "rounded-lg border border-border bg-card p-6 shadow-sm"

  return (
    <section
      className={containerClasses}
      onClick={isCompact ? () => onNavigateTab?.("credentials-onchain") : undefined}
      role={isCompact ? "button" : undefined}
      tabIndex={isCompact ? 0 : undefined}
    >
      <div className="mb-4 flex items-center justify-between">
        <h3 className="text-lg font-semibold text-foreground">
          On-chain Credential Activity
        </h3>
        {isCompact && (
          <span className="text-sm text-muted-foreground">View Credential Infrastructure →</span>
        )}
      </div>

      {enabled ? (
        <EnabledState stats={organization?.onChainStats} variant={variant} />
      ) : (
        <EmptyState variant={variant} onNavigateTab={onNavigateTab} />
      )}
    </section>
  )
}

export default OnChainCredentialsSection